import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useRouteMatch, withRouter } from 'react-router';
import { Layout, Row, Col, message, Modal, Drawer, Button, Popconfirm } from 'antd';
import posthog from 'posthog-js';
import { InvoiceFormBase, createQuoteConfig } from '../InvoiceFormBase';
import OWPdfViewer from '../ow_pdf_viewer/OWPdfViewer';
import QuoteUploadandParse, {
	QuoteLoadingSkeletonAnimated,
} from '../quote_uploader/QuoteUploadandParse';
import {
	quotesRestCrudThunksForSupplier,
	quotesRestCrudThunksForBuyer,
} from '../../thunks/quotes_thunks';
import {
	createProposalSubContractor,
	updateProposalSubContractor,
} from '../../thunks/proposals_thunks';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { getLocationSearch } from '../../utils/HistoryUtils';
import {
	applyParsedQuoteToForm,
	buildQuotePayloadFromFormValues,
	compareParsedQuoteWithFinal,
} from '../../utils/QuoteFormUtils';
import {
	getCurrencyMismatch,
	getWorkOrderCheck,
	getWorkOrderCheckOutcome,
	shouldSurfaceWorkOrderCheck,
} from '../../utils/InvoiceUtils';
import { isQuoteWorkOrderCheckEnabled } from '../../utils/FeatureFlagUtils';
import { WorkOrderCheckModal } from '../invoice_work_order_check/WorkOrderCheckModal';
import { WorkOrderCheck } from '../../types/InvoiceDecisionTypes';
import { isTaxExcludedFromQuotesByBuyer } from '../../utils/QuoteDataAdapter';
import {
	getCurrency,
	getEntityById,
	isSubContractor,
	nullSafeGet,
} from '../../utils/DataAccessUtils';
import ClearErrors from '../clear_errors/ClearErrors';
import { PROPOSALS_CRUD_ACTION_CREATORS } from '../../actions/proposals_actions';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { withUserType } from '../withUserType';
import ResizableSplitPanel from '../common/ResizableSplitPanel';
import { isInternalTech } from '../../utils/AuthUtils';
import { getContractorWO } from '../../utils/WorkOrderUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';

interface HeldWorkOrderCheck {
	workOrderCheck: WorkOrderCheck;
	parsedData: any;
}

const queryString = require('qs');
const { Content } = Layout;

const ProposalAIForm: FC<any> = ({
	createQuote,
	updateQuote,
	createContractorQuote,
	updateContractorQuote,
	userType,
	currentUser,
	workOrder,
	creating,
	updating,
	createErrors,
	updateErrors,
	isClientView,
	// Called after a successful create/update so the embedding quotes tab can
	// re-fetch and render the new quote (which unmounts this page + drawer). When
	// omitted, we fall back to a route push.
	onCreated,
}): React.ReactElement => {
	const [quoteFormData, setQuoteFormData] = useState<any>({});
	const [pdfUrl, setPdfUrl] = useState(null);
	// The raw File selected by the user. react-pdf renders a File/Blob far more
	// reliably than a large base64 data-URL string, so we prefer it for the preview.
	const [pdfFile, setPdfFile] = useState<any>(null);
	const [formKey, setFormKey] = useState(0);
	// Bumped on user-initiated "start over" events (delete, reset, error,
	// currency mismatch). NOT bumped on parse-complete — remounting the uploader
	// mid-S3 would orphan the in-flight upload, and we'd lose the proposalPdfLink.
	const [uploaderKey, setUploaderKey] = useState(0);
	// The backend's "is this quote for this work order" verdict: pending while the modal asks,
	// acknowledged once the uploader chose to continue, so the form keeps showing the evidence.
	const [pendingWorkOrderCheck, setPendingWorkOrderCheck] = useState<HeldWorkOrderCheck | null>(
		null
	);
	const [acknowledgedWorkOrderCheck, setAcknowledgedWorkOrderCheck] =
		useState<HeldWorkOrderCheck | null>(null);
	const [parsedDiscount, setParsedDiscount] = useState(null);
	const [parsedFormDataSnapshot, setParsedFormDataSnapshot] = useState<any>(null);
	const [parsingLoading, setParsingLoading] = useState(false);
	const [showDrawer, setShowDrawer] = useState(false);
	const [disableCosts, setDisableCosts] = useState(false);
	const location = useLocation();
	const history = useHistory();
	const match = useRouteMatch();

	// Quotes are proposed in the assigned supplier's currency (mirrors ProposalForm).
	const currency = useMemo(
		() =>
			getCurrency({ supplier: nullSafeGet('supplierFacility', workOrder) }) ||
			getCurrency({ workOrder }),
		[workOrder]
	);

	// Fires when the S3 upload completes. proposalPdfLink is only ever set from
	// here, so it's always a real URL — never the local base64 preview.
	const handleS3UrlReady = useCallback((s3Url: string) => {
		setQuoteFormData((prev) => ({ ...prev, proposalPdfLink: s3Url }));
		setPdfUrl(s3Url);
	}, []);

	// Fires after S3 retries are exhausted. The PDF never reached S3, so there is no
	// proposalPdfLink to submit with. If the parse had already opened the review
	// drawer, leaving it open would strand the user on a form that can never submit
	// ("Quote PDF is still uploading") with the retry uploader hidden behind it — so
	// we close and reset the drawer back to the uploader (like the parse-error/delete
	// paths), surface the failure as a toast, and remount the dragger for a fresh try.
	const handleS3UploadError = useCallback(() => {
		message.error('Failed to upload quote PDF. Please try uploading again.');
		setPendingWorkOrderCheck(null);
		setAcknowledgedWorkOrderCheck(null);
		setShowDrawer(false);
		setParsingLoading(false);
		setParsedDiscount(null);
		setParsedFormDataSnapshot(null);
		setDisableCosts(false);
		setPdfUrl(null);
		setPdfFile(null);
		setQuoteFormData((prev) => (prev ? { ...prev, proposalPdfLink: undefined } : prev));
		setFormKey((prev) => prev + 1);
		setUploaderKey((prev) => prev + 1);
	}, []);

	const workOrderId = useMemo(() => {
		const searchString = getLocationSearch(location);
		const queryParams = queryString.parse(searchString);
		return queryParams.workOrderId || match.params.id;
	}, [location, match.params.id]);

	useEffect(() => {
		if (workOrderId) {
			setQuoteFormData((prevFormData) => {
				if (prevFormData.workOrderId !== workOrderId) {
					return {
						...prevFormData,
						workOrderId: workOrderId,
						supplierFacilityId:
							workOrder && workOrder.supplierFacilityId
								? workOrder.supplierFacilityId
								: prevFormData.supplierFacilityId,
					};
				}
				return prevFormData;
			});
		}
	}, [workOrderId, workOrder]);

	const handleDeletePdf = useCallback(() => {
		if (parsedFormDataSnapshot) {
			posthog.capture('quote_pdf_deleted_after_parsing', { user_type: userType });
		}
		setPdfUrl(null);
		setPdfFile(null);
		setParsedDiscount(null);
		setParsedFormDataSnapshot(null);
		setAcknowledgedWorkOrderCheck(null);
		setParsingLoading(false);
		setFormKey((prev) => prev + 1);
		setUploaderKey((prev) => prev + 1);
		setShowDrawer(false);

		setQuoteFormData((prevFormData) => {
			if (prevFormData) {
				return { ...prevFormData, proposalPdfLink: undefined };
			}
			return prevFormData;
		});
	}, [parsedFormDataSnapshot, userType]);

	const handleBackConfirm = useCallback(() => {
		handleDeletePdf();
	}, [handleDeletePdf]);

	const handleReset = useCallback(() => {
		setParsedDiscount(null);
		// The comparison describes the parse being discarded, not the quote entered by hand.
		setAcknowledgedWorkOrderCheck(null);
		setParsedFormDataSnapshot(null);
		setParsingLoading(false);
		setDisableCosts(false);

		// "Create from scratch" clears the parsed line items for manual entry, but the
		// uploaded PDF must be preserved: handleSubmit requires proposalPdfLink, and the
		// manual quote should still carry the PDF (and keep showing its preview, so we
		// leave pdfFile/pdfUrl and the uploader instance untouched).
		setQuoteFormData((prev) => ({
			workOrderId: workOrderId,
			supplierFacilityId:
				workOrder && workOrder.supplierFacilityId ? workOrder.supplierFacilityId : undefined,
			proposalPdfLink: prev ? prev.proposalPdfLink : undefined,
		}));

		setFormKey((prev) => prev + 1);

		posthog.capture('quote_form_reset', {
			user_type: userType,
			had_parsed_data: !!parsedFormDataSnapshot,
		});
	}, [workOrderId, workOrder, userType, parsedFormDataSnapshot]);

	const handleFileSelected = useCallback((fileData) => {
		if (fileData.fileUrl || fileData.fileObject) {
			if (fileData.fileObject) {
				setPdfFile(fileData.fileObject);
			}
			setPdfUrl(fileData.fileUrl);
			setParsingLoading(true);
			setShowDrawer(true);
		} else {
			console.error('handleFileSelected: fileUrl and fileObject are undefined');
		}
	}, []);

	const handleUploadError = useCallback(
		(error = {}) => {
			message.error('Error parsing quote. Please try again.');
			setPendingWorkOrderCheck(null);
			setAcknowledgedWorkOrderCheck(null);
			setParsingLoading(false);
			setShowDrawer(false);
			setFormKey((prev) => prev + 1);
			setUploaderKey((prev) => prev + 1);
			// Drop any link from a prior upload so a follow-up can't submit with a stale PDF.
			setQuoteFormData((prev) => (prev ? { ...prev, proposalPdfLink: undefined } : prev));
			posthog.capture('quote_pdf_parse_error', {
				user_type: userType,
				error: (error as any).message,
			});
		},
		[userType]
	);

	const handleParsingStart = useCallback(() => {
		setParsingLoading(true);
	}, []);

	const handleParsingComplete = useCallback(() => {
		setParsingLoading(false);
	}, []);

	const applyParse = useCallback(
		(parsedData) => {
			applyParsedQuoteToForm({
				parsedData,
				workOrder,
				userType,
				setQuoteFormData,
				setParsedFormDataSnapshot,
				setParsedDiscount,
				setDisableCosts,
			});
			setFormKey((prev) => prev + 1);
			setShowDrawer(true);
		},
		[workOrder, userType]
	);

	const handleUploadSuccess = useCallback(
		(data) => {
			if (!data.parsedData) {
				return;
			}
			setAcknowledgedWorkOrderCheck(null);

			const mismatch = getCurrencyMismatch(data.parsedData, currency);
			if (mismatch) {
				// The quote's currency is the assigned supplier's, so only that supplier's own users can fix
				// it from company settings — not an internal tech or a subcontractor working under them.
				const canChangeCompanyCurrency =
					userType === ROLE_TYPES.SUPPLIER &&
					!isInternalTech(currentUser) &&
					!isSubContractor(currentUser, workOrder);
				setPdfUrl(null);
				setParsingLoading(false);
				setShowDrawer(false);
				setFormKey((prev) => prev + 1);
				setUploaderKey((prev) => prev + 1);
				// Drop the wrong-currency PDF's link so it can't get submitted later.
				setQuoteFormData((prev) => (prev ? { ...prev, proposalPdfLink: undefined } : prev));
				const mismatchMessage = `The uploaded quote is in ${mismatch.parsedCurrency}, but your account is configured to use ${mismatch.systemCurrency}.`;
				// The settings link lives in the content rather than on Cancel so that Cancel / Escape
				// only dismiss the warning and keep the user on the work order.
				const modal = Modal.confirm({
					title: 'Currency Mismatch',
					content: canChangeCompanyCurrency ? (
						<div>
							<div>{mismatchMessage}</div>
							<Button
								type="link"
								className="px-0"
								onClick={() => {
									modal.destroy();
									posthog.capture('quote_pdf_currency_mismatch_change_currency', {
										user_type: userType,
										parsed_currency: mismatch.parsedCurrency,
										system_currency: mismatch.systemCurrency,
									});
									history.push('/supplier/company/me');
								}}
							>
								Change Company Currency
							</Button>
						</div>
					) : (
						mismatchMessage
					),
					okText: 'Create from Scratch',
					cancelText: 'Cancel',
					onOk: () => {
						posthog.capture('quote_pdf_currency_mismatch_create_from_scratch', {
							user_type: userType,
							parsed_currency: mismatch.parsedCurrency,
							system_currency: mismatch.systemCurrency,
						});
						history.push(`/${userType}/workOrders/detail/${workOrderId}/newProposal`);
					},
					width: '50vw',
					centered: true,
				});
				return;
			}

			// Ask before applying the parse, as invoices do: a quote uploaded against the wrong work
			// order is cheaper to abandon than to undo. Never blocks - continuing is always available.
			const workOrderCheck = getWorkOrderCheck(data.parsedData);
			if (shouldSurfaceWorkOrderCheck(workOrderCheck)) {
				setPendingWorkOrderCheck({ workOrderCheck, parsedData: data.parsedData });
				posthog.capture('quote_work_order_mismatch_shown', {
					user_type: userType,
					work_order_id: workOrderId,
					outcome: getWorkOrderCheckOutcome(workOrderCheck),
				});
				return;
			}

			applyParse(data.parsedData);
		},
		[applyParse, workOrder, userType, currency, history, workOrderId, currentUser]
	);

	const handleWorkOrderCheckContinue = useCallback(() => {
		if (!pendingWorkOrderCheck) {
			return;
		}
		posthog.capture('quote_work_order_mismatch_continued', {
			user_type: userType,
			work_order_id: workOrderId,
			outcome: getWorkOrderCheckOutcome(pendingWorkOrderCheck.workOrderCheck),
		});
		setAcknowledgedWorkOrderCheck(pendingWorkOrderCheck);
		applyParse(pendingWorkOrderCheck.parsedData);
		setPendingWorkOrderCheck(null);
	}, [pendingWorkOrderCheck, applyParse, userType, workOrderId]);

	const handleWorkOrderCheckCancel = useCallback(() => {
		posthog.capture('quote_work_order_mismatch_cancelled', {
			user_type: userType,
			work_order_id: workOrderId,
		});
		setPendingWorkOrderCheck(null);
		handleDeletePdf();
	}, [handleDeletePdf, userType, workOrderId]);

	const onSuccess = useCallback(
		(record) => {
			setShowDrawer(false);
			if (onCreated) {
				onCreated(record);
			} else {
				history.push(`/${userType}/workOrders/detail/${workOrderId}/quotes`);
			}
		},
		[history, userType, workOrderId, onCreated]
	);

	const handleSubmit = useCallback(
		async (values, formInstance, scope) => {
			const currentPdfLink = nullSafeGet('proposalPdfLink', quoteFormData);
			if (!currentPdfLink) {
				message.error('Quote PDF is still uploading. Please wait a moment and try again.');
				return;
			}

			const isUpdate = values.id ? true : false;
			const submitAction = isUpdate ? updateQuote : createQuote;
			const submitContractorAction = isUpdate ? updateContractorQuote : createContractorQuote;
			const contractorWO = getContractorWO(workOrder, isClientView);
			const allFormValues = formInstance.getFieldsValue(true);
			const formValues = { ...allFormValues, ...values };

			const isBuyerUploaded = userType === ROLE_TYPES.BUYER || isInternalTech(currentUser);
			const status = isBuyerUploaded ? 'pending' : 'draft';
			const supplierFacilityId = nullSafeGet('supplierFacilityId', workOrder);

			const quote = buildQuotePayloadFromFormValues(formValues, scope, {
				workOrderId,
				currentUser,
				isBuyerUploaded,
				status,
				excludeTax: isTaxExcludedFromQuotesByBuyer(workOrder),
				baseMeta: {
					proposalPdfLink: currentPdfLink,
					supplierFacilityId,
				},
			});

			try {
				const finalQuote = isUpdate ? { ...quoteFormData, ...quote } : quote;

				if (parsedFormDataSnapshot) {
					const changeMetrics = compareParsedQuoteWithFinal(parsedFormDataSnapshot, finalQuote);
					const hadEdits =
						nullSafeGet('total_fields_changed', changeMetrics) > 0 ||
						nullSafeGet('total_line_items_changed', changeMetrics) > 0;

					posthog.capture(
						isUpdate ? 'quote_updated_from_parsed_pdf' : 'quote_created_from_parsed_pdf',
						{
							user_type: userType,
							was_parsed: true,
							user_changed_fields: hadEdits,
							...changeMetrics,
						}
					);
					finalQuote.isAiParsed = true;
					finalQuote.isAiParsedEdited = hadEdits;
				}

				if (formValues.signatureName) {
					finalQuote.aiParseMismatchSignedByEmail = currentUser.email;
				}

				if (
					userType === ROLE_TYPES.SUPPLIER &&
					contractorWO &&
					isSubContractor(currentUser, workOrder)
				) {
					const record = await submitContractorAction({
						...finalQuote,
						workOrderId: undefined,
						supplierFacilityId: undefined,
						contractorWorkOrderId: contractorWO.id,
						contractorSupplierFacilityId: contractorWO.contractorSupplierFacilityId,
						subSupplierFacilityId: contractorWO.subSupplierFacilityId,
					});
					onSuccess(record);
				} else {
					const record = await submitAction(finalQuote);
					onSuccess(record);
				}
			} catch (err) {
				if (
					typeof err === 'string' &&
					err.startsWith('Proposal already exists for this Work order')
				) {
					message.error(err);
					history.goBack();
				}
				throw err;
			}
		},
		[
			updateQuote,
			createQuote,
			updateContractorQuote,
			createContractorQuote,
			quoteFormData,
			currentUser,
			onSuccess,
			history,
			parsedFormDataSnapshot,
			userType,
			workOrderId,
			workOrder,
			isClientView,
		]
	);

	const formConfig = useMemo(() => {
		if (!quoteFormData || !workOrder || !currentUser) {
			return null;
		}

		return createQuoteConfig(quoteFormData, workOrder, currentUser, history, handleSubmit, {
			creating,
			updating,
			createErrors,
			updateErrors,
			parsedDataSnapshot: parsedFormDataSnapshot,
			workOrderCheck: nullSafeGet('workOrderCheck', acknowledgedWorkOrderCheck),
			rawParsedData: nullSafeGet('parsedData', acknowledgedWorkOrderCheck),
			disableCosts,
			onReset: handleReset,
			currency,
			isClientView,
		});
	}, [
		quoteFormData,
		workOrder,
		currentUser,
		history,
		handleSubmit,
		creating,
		updating,
		createErrors,
		updateErrors,
		parsedFormDataSnapshot,
		acknowledgedWorkOrderCheck,
		disableCosts,
		handleReset,
		currency,
		isClientView,
	]);

	if (!formConfig) {
		return <PageLoadingPlaceholder />;
	}

	return (
		<Content>
			<QuoteUploadandParse
				key={uploaderKey}
				// Withheld when the flag is off: the backend only runs the work order check for an
				// upload that names one, so this single omission disables the whole feature.
				workOrderId={isQuoteWorkOrderCheckEnabled() ? workOrderId : undefined}
				handleUploadSuccess={handleUploadSuccess}
				onFileSelected={handleFileSelected}
				onParsingStart={handleParsingStart}
				onParsingComplete={handleParsingComplete}
				onS3UrlReady={handleS3UrlReady}
				onS3UploadError={handleS3UploadError}
				uploaderType="dragger"
				onError={handleUploadError}
			/>
			<WorkOrderCheckModal
				visible={!!pendingWorkOrderCheck}
				workOrderCheck={nullSafeGet('workOrderCheck', pendingWorkOrderCheck)}
				parsedData={nullSafeGet('parsedData', pendingWorkOrderCheck)}
				workOrder={workOrder}
				documentLabel="Quote"
				onContinue={handleWorkOrderCheckContinue}
				onCancel={handleWorkOrderCheckCancel}
			/>
			<Drawer
				width="100vw"
				placement="right"
				visible={showDrawer}
				bodyStyle={{ height: '100vh', padding: 0, overflow: 'hidden' }}
				style={{ top: 0, height: '100vh' }}
				className="fullscreen-drawer"
				closeIcon={
					<Popconfirm
						title="Are you sure you want to go back? Any unsaved changes will be lost."
						onConfirm={handleBackConfirm}
						okText="Yes"
						cancelText="No"
					>
						<Button type="ghost" icon={<ArrowLeftOutlined />}>
							Back
						</Button>
					</Popconfirm>
				}
				title={<div className="h3medium">Create Quote</div>}
			>
				<Row style={{ height: '100%' }}>
					<ClearErrors actionCreators={PROPOSALS_CRUD_ACTION_CREATORS} />
					<Col span={24} style={{ height: '100%' }}>
						<ResizableSplitPanel
							leftPanel={
								<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
									<OWPdfViewer
										file={pdfFile || pdfUrl || null}
										fileName="Quote.pdf"
										showDelete={true}
										onDelete={handleDeletePdf}
									/>
								</div>
							}
							rightPanel={
								<div>
									{parsingLoading ? (
										<QuoteLoadingSkeletonAnimated />
									) : (
										<InvoiceFormBase
											key={formKey}
											config={formConfig}
											handleCancel={handleDeletePdf}
										/>
									)}
								</div>
							}
							rightPanelClassName="flex-1 min-h-0 overflow-y-auto p-6 pt-7"
						/>
					</Col>
				</Row>
			</Drawer>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	workOrder: getEntityById(ownProps.match.params.id, state.work_orders),
	creating: state.proposals.creating,
	updating: state.proposals.updating,
	createErrors: state.proposals.createErrors,
	updateErrors: state.proposals.updateErrors,
	isClientView: state.clientView.isClientView,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	createQuote: (quote) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? quotesRestCrudThunksForSupplier.create(quote)
				: quotesRestCrudThunksForBuyer.create(quote)
		),
	updateQuote: (quote) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? quotesRestCrudThunksForSupplier.update(quote)
				: quotesRestCrudThunksForBuyer.update(quote)
		),
	createContractorQuote: (quote) => dispatch(createProposalSubContractor(quote)),
	updateContractorQuote: (quote) => dispatch(updateProposalSubContractor(quote)),
});

export default withUserType(
	withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(ProposalAIForm))
);
